import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../helpers/api_helper';

// Types
export interface CreateUserPayload {
  operation: 'create';
  mobile_number: string;
  password: string;
  first_name: string;
  last_name: string;
  role_id: number;
  initial_points?: number;
  app_id: string;
  is_staff: boolean;
  is_active: boolean;
}

export interface UserResponse {
  operation: string;
  message: string;
  user: {
    mobile_number: string;
    first_name: string;
    last_name: string;
    balance: number;
    balance_breakdown: any[];
    points: number;
  };
  initial_points: number;
  is_staff: boolean;
  role: {
    id: number;
    name: string;
  };
}

export interface Role {
  id: number;
  name: string;
  description?: string;
}

export interface CreateRolePayload {
  name: string;
}

export interface RoleResponse {
  id: number;
  name: string;
  message?: string;
}

export interface UserManagementState {
  users: any[];
  roles: Role[];
  loading: boolean;
  error: string | null;
  createUserLoading: boolean;
  createUserError: string | null;
  lastCreatedUser: UserResponse | null;
  createRoleLoading: boolean;
  createRoleError: string | null;
  lastCreatedRole: RoleResponse | null;
}

const initialState: UserManagementState = {
  users: [],
  roles: [],
  loading: false,
  error: null,
  createUserLoading: false,
  createUserError: null,
  lastCreatedUser: null,
  createRoleLoading: false,
  createRoleError: null,
  lastCreatedRole: null,
};

// Async thunks
export const fetchRoles = createAsyncThunk(
  'userManagement/fetchRoles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/roles/');
      return response.data.roles || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch roles');
    }
  }
);

export const createUser = createAsyncThunk(
  'userManagement/createUser',
  async (userData: CreateUserPayload, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/users/', userData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to create user');
    }
  }
);

export const createRole = createAsyncThunk(
  'userManagement/createRole',
  async (roleData: CreateRolePayload, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/roles/', roleData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to create role');
    }
  }
);

export const fetchUsers = createAsyncThunk(
  'userManagement/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/users/');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch users');
    }
  }
);

// Slice
const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    clearCreateUserError: (state) => {
      state.createUserError = null;
    },
    clearLastCreatedUser: (state) => {
      state.lastCreatedUser = null;
    },
    clearCreateRoleError: (state) => {
      state.createRoleError = null;
    },
    clearLastCreatedRole: (state) => {
      state.lastCreatedRole = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch roles
    builder
      .addCase(fetchRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.roles = action.payload.results || action.payload;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create user
    builder
      .addCase(createUser.pending, (state) => {
        state.createUserLoading = true;
        state.createUserError = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.createUserLoading = false;
        state.lastCreatedUser = action.payload;
        // Add the new user to the users list
        if (action.payload.user) {
          state.users.unshift(action.payload.user);
        }
      })
      .addCase(createUser.rejected, (state, action) => {
        state.createUserLoading = false;
        state.createUserError = action.payload as string;
      });

    // Fetch users
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.results || action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create role
    builder
      .addCase(createRole.pending, (state) => {
        state.createRoleLoading = true;
        state.createRoleError = null;
      })
      .addCase(createRole.fulfilled, (state, action) => {
        state.createRoleLoading = false;
        state.lastCreatedRole = action.payload;
        // Add the new role to the roles list
        if (action.payload.id && action.payload.name) {
          state.roles.unshift(action.payload);
        }
      })
      .addCase(createRole.rejected, (state, action) => {
        state.createRoleLoading = false;
        state.createRoleError = action.payload as string;
      });
  },
});

export const { clearCreateUserError, clearLastCreatedUser, clearCreateRoleError, clearLastCreatedRole } = userManagementSlice.actions;
export default userManagementSlice.reducer;
